import React, { memo, useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Card, CardBody, CardTitle } from "reactstrap";
import RiskMatrixTable from "src/modules/data-protection/components/RisksAndTasks/risks/matrix";
import RiskAssessmentService from "src/modules/data-protection/apis/RiskAssessmentService";

const mergeRiskMatrixData = (matrixConfig, dashboardMatrix) => {
    if (!matrixConfig?.matrix?.length) {
        return {
            matrix: dashboardMatrix?.matrix || [],
            consequences: dashboardMatrix?.consequences || [],
            likelihoods: dashboardMatrix?.likelihoods || [],
        };
    }

    const dashboardCells = (dashboardMatrix?.matrix || []).flat();
    const dashboardCellMap = new Map(
        dashboardCells.map((cell) => [cell.id, cell])
    );

    return {
        matrix: matrixConfig.matrix.map((row) =>
            row.map((cell) => {
                const dashboardCell = dashboardCellMap.get(cell.id);

                return {
                    ...cell,
                    risks: dashboardCell?.risks || [],
                };
            })
        ),
        consequences: matrixConfig.consequences || dashboardMatrix?.consequences || [],
        likelihoods: matrixConfig.likelihoods || dashboardMatrix?.likelihoods || [],
    };
};

const RiskMatrix = memo(({ t, data, isFetching }) => {
    const handleFetchMatrixConfigQuery = useQuery({
        queryKey: ["ai-governance-risk-matrix-config"],
        queryFn: async () => {
            const service = RiskAssessmentService.getInstance();
            return await service.fetchMatrix();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });

    const matrixData = useMemo(
        () => mergeRiskMatrixData(handleFetchMatrixConfigQuery.data, data),
        [handleFetchMatrixConfigQuery.data, data]
    );

    const isLoading = isFetching || handleFetchMatrixConfigQuery.isFetching;

    if (isLoading) {
        return (
            <Card className="mb-3 card_box_Shadow h-100 w-100">
                <CardBody>
                    <div className="dt-field dt-skeleton mb-3" style={{ width: "25%", height: "24px" }} />
                    <div className="dt-field dt-skeleton" style={{ width: "100%", height: "340px" }} />
                </CardBody>
            </Card>
        );
    }

    return (
        <Card className="mb-3 card_box_Shadow h-100 w-100">
            <CardBody className="d-flex flex-column">
                <CardTitle tag="h6" className="mb-3">
                    {t("Risk Matrix")}
                </CardTitle>
                <div className="flex-grow-1 table-responsive w-100" style={{ overflowX: "auto" }}>
                    <RiskMatrixTable
                        matrix={matrixData.matrix}
                        consequences={matrixData.consequences}
                        likelihoods={matrixData.likelihoods}
                        isLoading={false}
                    />
                </div>
            </CardBody>
        </Card>
    );
});

export default withNamespaces()(RiskMatrix);
